/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { CheckoutErrorType } from '../../core/enums/checkout-error-type.enum';
import { CheckoutSuccessType } from '../../core/enums/checkout-success-type.enum';
import { ErrorMetadata } from '../../models/ui/error-metadata.model';
import { ErrorState } from '../../models/ui/error-state.model';
import { SuccessMetadata } from '../../models/ui/success-metadata.model';
import { SuccessState } from '../../models/ui/success-state.model';

const INITIAL_ERROR_STATE: ErrorState = {
  path: '',
  type: null,
  isRetryable: false,
  metadata: {}
};

const INITIAL_SUCCESS_STATE: SuccessState = {
  type: null,
  metadata: {}
};

@Injectable({
  providedIn: 'root',
})
export class UIStateService {

  private errorStateSubject = new BehaviorSubject<ErrorState>(INITIAL_ERROR_STATE);
  private successStateSubject = new BehaviorSubject<SuccessState>(INITIAL_SUCCESS_STATE);

  readonly errorState$: Observable<ErrorState> = this.errorStateSubject.asObservable();
  readonly successState$: Observable<SuccessState> = this.successStateSubject.asObservable();

  constructor(
    private router: Router
  ) {
    // Angular DI
  }

  showError(type: CheckoutErrorType, metadata?: ErrorMetadata, isRetryable = false): void {
    this.errorStateSubject.next({
      type,
      isRetryable,
      path: this.router.url,
      metadata: metadata || {}
    });
    if (metadata && metadata.embeddedErrorPage) {
      return;
    }
    this.router.navigate(['/error']);
  }

  clearError(): void {
    this.errorStateSubject.next(INITIAL_ERROR_STATE);
  }

  showSuccess(type: CheckoutSuccessType, metadata?: SuccessMetadata): void {
    this.successStateSubject.next({
      type,
      metadata: metadata || {}
    });
    this.router.navigate(['/success']);
  }

  clearSuccess(): void {
    this.successStateSubject.next(INITIAL_SUCCESS_STATE);
  }

  getErrorState(): ErrorState {
    return this.errorStateSubject.value;
  }

  getSuccessState(): SuccessState {
    return this.successStateSubject.value;
  }
}
